// The React app is deployed as-is under /v2 (its own container and pipeline, wired at the
// nginx/ALB level in front of both apps). We never own or change that prefix — we only point
// an iframe at it, so /v2/* keeps working as the standalone React application.

export const REACT_APP_BASE = '/v2'
export const REACT_APP_EMBED_PARAM = 'embed'

// Top-level paths hosted by the React app. Must stay in sync with the routes declared in
// react/src/App.tsx and with REACT_APP_ROUTES (./routes.ts).
export const REACT_APP_ROOT_PATHS = ['documents', 'invoices', 'replies']

export function stripQueryAndHash (url: string): string {
  return url.split('?')[0].split('#')[0]
}

/**
 * /documents/abc/editor  ->  /v2/documents/abc/editor?embed=1
 *
 * The Angular route and the React route are deliberately the same path, so the iframe src is
 * derived from the Angular URL itself. That is what keeps hard refreshes and bookmarks working.
 */
export function buildReactAppUrl (angularUrl: string): string {
  const path = stripQueryAndHash(angularUrl)
  const normalized = path.charAt(0) === '/' ? path : `/${path}`
  return `${REACT_APP_BASE}${normalized}?${REACT_APP_EMBED_PARAM}=1`
}

/**
 * Guards paths coming from the iframe before we write them into the browser URL bar.
 * Only paths this feature owns are accepted; anything else would break the back button.
 */
export function isReactAppPath (path: string): boolean {
  if (!isSameOriginPath(path)) {
    return false
  }
  const firstSegment = stripQueryAndHash(path).split('/')[1]
  return REACT_APP_ROOT_PATHS.indexOf(firstSegment) !== -1
}

/** Relative, same-origin path: no protocol-relative (?), no absolute URL. */
export function isSameOriginPath (path: string): boolean {
  return typeof path === 'string' &&
    path.charAt(0) === '/' &&
    path.charAt(1) !== '/' &&
    path.charAt(1) !== '\\'
}
